import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Search, FileSpreadsheet, MapPin, Calendar, Users, Hospital, Car, Briefcase } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { MultiSelectFilter } from '@/components/MultiSelectFilter';
import { DateField } from '@/components/common/DateField';
import { GRAVEDAD_COLOR, Stat, CorrCard, WorkerProfileSection } from './shared';

const API = process.env.REACT_APP_BACKEND_URL;

export function ListadoSection({ authHeader }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const refFromUrl = searchParams.get('ref');
  const [filters, setFilters] = useState({
    desde: '', hasta: '', search: '',
    grado: [], con_baja: [], lugar: [], sede: [], provincia: [],
  });
  const [selected, setSelected] = useState(null);
  const [exporting, setExporting] = useState(false);
  const params = (() => {
    const out = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (Array.isArray(v)) { if (v.length) out[k] = v.join(','); }
      else if (v !== '' && v !== null && v !== undefined) out[k] = v;
    });
    return out;
  })();
  const { data, isLoading } = useQuery({
    queryKey: ['accidentes-list', params],
    queryFn: () => axios.get(`${API}/api/accidentes`, { headers: authHeader(), params }).then(r => r.data),
  });
  const items = data?.items ?? [];
  const { data: facets } = useQuery({
    queryKey: ['accidentes-facets'],
    queryFn: () => axios.get(`${API}/api/accidentes/facets`, { headers: authHeader() }).then(r => r.data),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!refFromUrl || selected?.n_referencia === refFromUrl) return;
    axios.get(`${API}/api/accidentes/${encodeURIComponent(refFromUrl)}`, { headers: authHeader() })
      .then(r => setSelected(r.data))
      .catch(() => toast.error(`Accidente ${refFromUrl} no encontrado`));
  }, [refFromUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const closeDetail = () => {
    setSelected(null);
    if (refFromUrl) {
      const next = new URLSearchParams(searchParams);
      next.delete('ref');
      setSearchParams(next, { replace: true });
    }
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/api/accidentes/export-excel`, {
        headers: authHeader(), params, responseType: 'blob', timeout: 90000,
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.download = `accidentes_delta_${today}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Excel descargado (${data?.total || 0} filas)`);
    } catch { toast.error('Error al exportar a Excel'); }
    finally { setExporting(false); }
  };

  return (
    <div className="space-y-4" data-testid="accidentes-listado">
      <div className="flex flex-wrap gap-2 items-end">
        <div><Label className="text-[10px] uppercase mb-1" style={{ color: 'var(--vz-text-muted)' }}>Desde</Label><DateField value={filters.desde} onChange={v => setFilters({ ...filters, desde: v })} className="w-36" testId="acc-filter-desde" /></div>
        <div><Label className="text-[10px] uppercase mb-1" style={{ color: 'var(--vz-text-muted)' }}>Hasta</Label><DateField value={filters.hasta} onChange={v => setFilters({ ...filters, hasta: v })} className="w-36" testId="acc-filter-hasta" /></div>
        <div>
          <Label className="text-[10px] uppercase mb-1" style={{ color: 'var(--vz-text-muted)' }}>Gravedad</Label>
          <MultiSelectFilter options={[{value:'1',label:'Leve'},{value:'2',label:'Grave'},{value:'3',label:'Muy grave'},{value:'4',label:'Fallecimiento'}]} value={filters.grado} onChange={v => setFilters({...filters,grado:v})} placeholder="Todas" searchable={false} width="w-36" dataTestid="acc-filter-grado" />
        </div>
        <div>
          <Label className="text-[10px] uppercase mb-1" style={{ color: 'var(--vz-text-muted)' }}>Tipo</Label>
          <MultiSelectFilter options={[{value:'con_baja',label:'Con baja'},{value:'sin_baja',label:'Sin baja'},{value:'recaida',label:'Recaída'}]} value={filters.con_baja} onChange={v => setFilters({...filters,con_baja:v})} placeholder="Todos" searchable={false} width="w-32" dataTestid="acc-filter-con-baja" />
        </div>
        <div>
          <Label className="text-[10px] uppercase mb-1" style={{ color: 'var(--vz-text-muted)' }}>Subtipo (lugar)</Label>
          <MultiSelectFilter options={[{value:'centro_habitual',label:'Centro habitual'},{value:'in_itinere',label:'In itinere'},{value:'otra_empresa',label:'Otra empresa'},{value:'desplazamiento',label:'En desplazamiento'},{value:'blanco',label:'Sin clasificar'}]} value={filters.lugar} onChange={v => setFilters({...filters,lugar:v})} placeholder="Todos" searchable={false} width="w-40" dataTestid="acc-filter-lugar" />
        </div>
        <div>
          <Label className="text-[10px] uppercase mb-1" style={{ color: 'var(--vz-text-muted)' }}>Centro</Label>
          <MultiSelectFilter options={(facets?.sedes||[]).map(s=>({value:s.codigo,label:`${s.label||s.codigo} (${s.n})`}))} value={filters.sede} onChange={v => setFilters({...filters,sede:v})} placeholder="Todos" searchable width="w-48" dataTestid="acc-filter-sede" />
        </div>
        <div>
          <Label className="text-[10px] uppercase mb-1" style={{ color: 'var(--vz-text-muted)' }}>Provincia</Label>
          <MultiSelectFilter options={(facets?.provincias||[]).map(p=>({value:p,label:p}))} value={filters.provincia} onChange={v => setFilters({...filters,provincia:v})} placeholder="Todas" searchable width="w-40" dataTestid="acc-filter-provincia" />
        </div>
        <div className="flex-1 min-w-48 max-w-md">
          <Label className="text-[10px] uppercase mb-1" style={{ color: 'var(--vz-text-muted)' }}>Buscar</Label>
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--vz-text-muted)' }} />
            <Input value={filters.search} onChange={e => setFilters({...filters,search:e.target.value})} placeholder="Apellidos, razón social, municipio..." className="h-8 text-xs pl-8" data-testid="acc-filter-search" />
          </div>
        </div>
      </div>

      {isLoading ? <Skeleton className="h-60 w-full" /> : (
        <>
          <div className="flex items-center justify-between gap-3">
            <div className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>{data?.total || 0} accidentes encontrados</div>
            <Button size="sm" variant="outline" className="text-xs gap-1.5 h-8" disabled={exporting || !data?.total}
              onClick={exportExcel} data-testid="acc-export-excel-btn">
              <FileSpreadsheet size={11} /> {exporting ? 'Exportando...' : 'Exportar Excel'}
            </Button>
          </div>
          <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--vz-border-subtle)' }}>
            <Table>
              <TableHeader>
                <TableRow style={{ background: 'var(--vz-bg-elevated)', borderColor: 'var(--vz-border-subtle)' }}>
                  <TableHead className="text-xs">F. Accidente</TableHead>
                  <TableHead className="text-xs">F. Baja</TableHead>
                  <TableHead className="text-xs">F. Alta</TableHead>
                  <TableHead className="text-xs">Trabajador</TableHead>
                  <TableHead className="text-xs">Centro</TableHead>
                  <TableHead className="text-xs">Modo</TableHead>
                  <TableHead className="text-xs">Tipo</TableHead>
                  <TableHead className="text-xs">Subtipo</TableHead>
                  <TableHead className="text-xs">Gravedad</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.slice(0, 100).map((a, i) => (
                  <TableRow key={i} onClick={() => setSelected(a)} className="cursor-pointer hover:bg-white/5" style={{ borderColor: 'var(--vz-border-subtle)' }} data-testid={`acc-row-${a.n_referencia}`}>
                    <TableCell className="text-xs py-2 font-mono">{a.fecha_accidente || '—'}</TableCell>
                    <TableCell className="text-xs py-2 font-mono" style={{ color: 'var(--vz-text-muted)' }}>
                      {a.fecha_baja_medica_csv ? a.fecha_baja_medica_csv : (a.con_baja ? <span title="Estimada" style={{ color: '#F97316' }}>{a.fecha_accidente}</span> : '—')}
                    </TableCell>
                    <TableCell className="text-xs py-2 font-mono" style={{ color: (a.form_data?.fecha_alta_medica || a.fecha_alta_medica_csv) ? '#4CAF50' : 'var(--vz-text-muted)' }}>
                      {a.form_data?.fecha_alta_medica || a.fecha_alta_medica_csv || '—'}
                    </TableCell>
                    <TableCell className="text-xs py-2">{a.apellidos} {a.nombre}</TableCell>
                    <TableCell className="text-xs py-2" style={{ color: a.sede_codigo ? 'var(--vz-text-primary)' : '#F44336' }}>{a.sede_label || a.razon_social || '—'}</TableCell>
                    <TableCell className="text-xs py-2" style={{ color: 'var(--vz-text-muted)' }}>{a.modo_accidente || '—'}</TableCell>
                    <TableCell className="text-xs py-2">
                      {a.tipo_baja_label && a.tipo_baja_label !== '—' ? (
                        <Badge variant="outline" className="text-[9px]" style={{ borderColor: a.con_baja ? '#F44336' : a.tipo_accidente_o_recaida === 2 ? '#FF9800' : '#4CAF50', color: a.con_baja ? '#F44336' : a.tipo_accidente_o_recaida === 2 ? '#FF9800' : '#4CAF50' }}>{a.tipo_baja_label}</Badge>
                      ) : <span style={{ color: 'var(--vz-text-muted)' }}>—</span>}
                    </TableCell>
                    <TableCell className="text-xs py-2">
                      {a.lugar_accidente_label && a.lugar_accidente_label !== '—' ? (
                        <Badge variant="outline" className="text-[9px]" style={{ borderColor: a.lugar_accidente === 3 ? '#9C27B0' : a.lugar_accidente === 2 ? '#FF9800' : '#4A9EFF', color: a.lugar_accidente === 3 ? '#9C27B0' : a.lugar_accidente === 2 ? '#FF9800' : '#4A9EFF' }}>{a.lugar_accidente_label}</Badge>
                      ) : <span style={{ color: 'var(--vz-text-muted)' }}>—</span>}
                    </TableCell>
                    <TableCell className="text-xs py-2">
                      <Badge style={{ background: GRAVEDAD_COLOR[a.grado_lesion_label] + '20', color: GRAVEDAD_COLOR[a.grado_lesion_label] }}>{a.grado_lesion_label}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {items.length > 100 && <div className="text-[10px] py-2 text-center" style={{ color: 'var(--vz-text-muted)' }}>Mostrando 100 de {items.length}. Refina la búsqueda.</div>}
          </div>

          {selected && (
            <div data-testid="acc-detail-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }} onClick={closeDetail}>
              <div className="rounded-xl shadow-2xl max-w-5xl w-full max-h-[92vh] overflow-y-auto" style={{ background: 'var(--vz-bg-card)', border: '1px solid var(--vz-border-subtle)' }} onClick={e => e.stopPropagation()}>
                <DetalleCard acc={selected} authHeader={authHeader} onClose={closeDetail} />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function DetalleCard({ acc, authHeader, onClose }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(acc.form_data || {});
  const [saving, setSaving] = useState(false);
  const { data: corr } = useQuery({
    queryKey: ['acc-corr', acc.n_referencia],
    queryFn: () => axios.get(`${API}/api/accidentes/${acc.n_referencia}/correlacion`, { headers: authHeader() }).then(r => r.data),
  });
  const { data: profile } = useQuery({
    queryKey: ['acc-profile', acc.n_referencia],
    queryFn: () => axios.get(`${API}/api/accidentes/${acc.n_referencia}/worker-profile`, { headers: authHeader() }).then(r => r.data),
  });

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/api/accidentes/${acc.n_referencia}`, form, { headers: authHeader() });
      toast.success('Datos guardados');
      qc.invalidateQueries({ queryKey: ['accidentes-list'] });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-xl p-4 space-y-4 sticky top-4" style={{ background: 'var(--vz-bg-elevated)', border: '1px solid var(--vz-border-subtle)', maxHeight: '85vh', overflowY: 'auto' }}>
      <div className="flex justify-between items-start gap-2">
        <div>
          <h3 className="font-bold text-sm text-white">{acc.apellidos} {acc.nombre}</h3>
          <p className="text-[11px] mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>{acc.fecha_accidente} · {acc.razon_social}</p>
        </div>
        <button onClick={onClose} className="text-xs px-2 py-1 rounded hover:bg-white/10" style={{ color: 'var(--vz-text-muted)' }}>×</button>
      </div>
      <div className="grid grid-cols-2 gap-2 text-[11px]">
        <Stat icon={<MapPin size={11} />} label="Provincia" value={acc.provincia} />
        <Stat icon={<Calendar size={11} />} label="Hora" value={acc.hora_dia} />
        <Stat icon={<Users size={11} />} label="Plantilla" value={acc.plantilla_centro} />
        <Stat icon={<Hospital size={11} />} label="Hospitalizado" value={acc.hospitalizado ? 'Sí' : 'No'} color={acc.hospitalizado ? '#F44336' : null} />
        <Stat icon={<Car size={11} />} label="Tráfico" value={acc.es_trafico ? 'Sí' : 'No'} />
        <Stat icon={<Briefcase size={11} />} label="ETT" value={acc.es_ett ? 'Sí' : 'No'} />
      </div>
      <div className="space-y-1 text-[11px] pt-2 border-t" style={{ borderColor: 'var(--vz-border-subtle)' }}>
        <Stat label="Tipo herida" value={acc.tipo_herida} multiline />
        <Stat label="Modo accidente" value={acc.modo_accidente} multiline />
        <Stat label="Material causante" value={acc.material_causante} multiline />
        <Stat label="Lugar" value={acc.lugar_accidente} multiline />
      </div>
      <WorkerProfileSection profile={profile} />
      <div className="pt-2 border-t space-y-2" style={{ borderColor: 'var(--vz-border-subtle)' }}>
        <p className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--vz-text-muted)' }}>Actividad preventiva ±30 días</p>
        {!acc.sede_codigo && <p className="text-[11px]" style={{ color: '#F97316' }}>Sin centro Valoriza mapeado — no se puede correlar.</p>}
        {acc.sede_codigo && corr && (
          <div className="grid grid-cols-3 gap-2">
            <CorrCard color="#4A9EFF" label="OPS" n={corr.ops?.length || 0} />
            <CorrCard color="#00C853" label="Charlas" n={corr.liderazgo?.length || 0} />
            <CorrCard color="#FF9800" label="Near Miss" n={corr.nm?.length || 0} />
          </div>
        )}
      </div>
      <div className="pt-2 border-t space-y-2" style={{ borderColor: 'var(--vz-border-subtle)' }}>
        <p className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--vz-text-muted)' }}>Formulario SP&S</p>
        <div><Label className="text-[10px]">Causa raíz</Label><Textarea value={form.causa_raiz || ''} onChange={e => setForm({...form,causa_raiz:e.target.value})} className="text-xs min-h-16" data-testid="acc-form-causa-raiz" /></div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-[10px]">¿Evitable?</Label>
            <select value={form.evitable === true ? 'true' : form.evitable === false ? 'false' : ''} onChange={e => setForm({...form,evitable:e.target.value===''?null:e.target.value==='true'})} className="h-8 text-xs w-full px-2 rounded-md bg-transparent border" style={{ borderColor: 'var(--vz-border-subtle)' }}>
              <option value="" style={{ background: '#222' }}>—</option><option value="true" style={{ background: '#222' }}>Sí</option><option value="false" style={{ background: '#222' }}>No</option>
            </select></div>
          <div><Label className="text-[10px]">Gravedad real SP&S</Label>
            <select value={form.gravedad_real_sps || ''} onChange={e => setForm({...form,gravedad_real_sps:e.target.value?Number(e.target.value):null})} className="h-8 text-xs w-full px-2 rounded-md bg-transparent border" style={{ borderColor: 'var(--vz-border-subtle)' }}>
              <option value="" style={{ background: '#222' }}>—</option><option value="1" style={{ background: '#222' }}>Leve</option><option value="2" style={{ background: '#222' }}>Grave</option><option value="3" style={{ background: '#222' }}>Muy grave</option><option value="4" style={{ background: '#222' }}>Fallecimiento</option>
            </select></div>
        </div>
        <div><Label className="text-[10px]">Acción correctora</Label><Textarea value={form.accion_correctora || ''} onChange={e => setForm({...form,accion_correctora:e.target.value})} className="text-xs min-h-16" /></div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-[10px]">Responsable</Label><Input value={form.responsable_seguimiento || ''} onChange={e => setForm({...form,responsable_seguimiento:e.target.value})} className="h-8 text-xs" /></div>
          <div><Label className="text-[10px]">Fecha cierre</Label><Input type="date" value={form.fecha_cierre || ''} onChange={e => setForm({...form,fecha_cierre:e.target.value})} className="h-8 text-xs" /></div>
        </div>
        {acc.con_baja && (
          <div><Label className="text-[10px]">Fecha alta médica (vuelta al trabajo)</Label>
            <Input type="date" value={form.fecha_alta_medica || ''} onChange={e => setForm({...form,fecha_alta_medica:e.target.value})} className="h-8 text-xs" data-testid="acc-form-fecha-alta-medica" />
            {form.fecha_alta_medica && acc.fecha_accidente && (
              <p className="text-[9px] mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>
                Días de baja: {Math.max(0, Math.round((new Date(form.fecha_alta_medica) - new Date(acc.fecha_accidente)) / 86400000))}
              </p>
            )}
          </div>
        )}
        <div><Label className="text-[10px]">Lecciones aprendidas</Label><Textarea value={form.lecciones_aprendidas || ''} onChange={e => setForm({...form,lecciones_aprendidas:e.target.value})} className="text-xs min-h-16" /></div>
        <Button size="sm" className="w-full h-8 text-xs gap-1.5" onClick={save} disabled={saving} data-testid="acc-form-save">
          {saving ? 'Guardando...' : 'Guardar formulario SP&S'}
        </Button>
      </div>
    </div>
  );
}
